import React, { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import Sidebar from '../components/layout/Sidebar'
import QuickChartCreator from '../components/charts/QuickChartCreator'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Plus, LayoutDashboard, Loader2, BarChart3, Home as HomeIcon } from 'lucide-react'
import { currentOrganizationAtom } from '../store/organizationAtoms'
import { ChartService } from '../services/chartService'
import { DashboardService } from '../services/dashboardService'
import { userService } from '../services/firestoreService'
import type { User as UserProfileType } from '@shared/schemas/user'
import type { Dashboard, DashboardChartItem, DashboardLayoutConfiguration } from '@shared/types/dashboard.types'
import type { Chart, ChartStudy, ChartType, AxisOptions as SharedAxisOptions } from '@shared/schemas/chart'
import type { ChartData } from '../store/chartAtoms'
import DisplayChartCard from '../components/dashboard/DisplayChartCard'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { dashboardFormDialogAtom } from '../store/dashboardAtoms'
import type { CreateDashboardData } from '@shared/types/dashboard.types'
import ChartStudiesDialog from '../components/charts/ChartStudiesDialog'
import UniversalChartRenderer from '../components/charts/UniversalChartRenderer'
import { normalizeChartData } from '../lib/chartDataNormalizer'
import { convertDatabaseChartToUniversal } from '@shared/schemas/chart'
import { useTheme } from '../lib/themeUtils'
import { useCustomPalettes } from '../contexts/ChartPreferencesContext'

interface DisplayableDashboardChartOnHome extends Chart {
  order: number
}

const Home: React.FC = () => {
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const [currentOrganization] = useAtom(currentOrganizationAtom)
  const customPalettes = useCustomPalettes()
  const isDarkMode = useTheme()
  
  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null)
  const [defaultDashboard, setDefaultDashboard] = useState<Dashboard | null>(null)
  const [displayedCharts, setDisplayedCharts] = useState<DisplayableDashboardChartOnHome[]>([])
  
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false)
  const [isLoadingCharts, setIsLoadingCharts] = useState(false)
  
  const [showChartCreator, setShowChartCreator] = useState(false)
  const [userChartCount, setUserChartCount] = useState(0)

  // State for the Create Dashboard Dialog on Home page
  const [createDashboardDialogState, setCreateDashboardDialogState] = useAtom(dashboardFormDialogAtom)
  const [dashboardTitle, setDashboardTitle] = useState('')
  const [dashboardDescription, setDashboardDescription] = useState('')
  const [isSubmittingDashboard, setIsSubmittingDashboard] = useState(false)

  // State for ChartStudiesDialog on Home page
  const [studiesDialogState, setStudiesDialogState] = useState<{
    isOpen: boolean;
    chart: Chart | null; 
    isDatabase: boolean; 
  }>({ isOpen: false, chart: null, isDatabase: true });

  useEffect(() => {
    if (currentUser?.uid && currentOrganization?.id) {
      setIsLoadingProfile(true)
      Promise.all([
        userService.getById(currentUser.uid),
        ChartService.getUserCharts(currentOrganization.id, currentUser.uid)
      ]).then(([profile, userChartsData]) => {
        setUserProfile(profile)
        setUserChartCount(userChartsData.length)
      }).catch(error => {
        console.error("Error fetching user profile or chart count:", error)
        setUserProfile(null)
        setUserChartCount(0)
      }).finally(() => {
        setIsLoadingProfile(false)
      })
    } else {
      setUserProfile(null)
      setUserChartCount(0)
      setIsLoadingProfile(false)
    }
  }, [currentUser, currentOrganization])

  useEffect(() => {
    if (userProfile?.defaultDashboardId && currentUser?.uid && currentOrganization?.id) {
      setIsLoadingDashboard(true)
      DashboardService.getDashboard(currentOrganization.id, currentUser.uid, userProfile.defaultDashboardId)
        .then(dashboard => {
          setDefaultDashboard(dashboard)
        })
        .catch(error => {
          console.error("Error fetching default dashboard:", error)
          setDefaultDashboard(null)
        })
        .finally(() => {
          setIsLoadingDashboard(false)
        })
    } else {
      setDefaultDashboard(null)
      setIsLoadingDashboard(false)
    }
  }, [userProfile, currentUser, currentOrganization])

  useEffect(() => {
    if (defaultDashboard && defaultDashboard.charts.length > 0 && currentUser?.uid && currentOrganization?.id) {
      setIsLoadingCharts(true)
      const chartPromises = defaultDashboard.charts.map(item =>
        ChartService.getById(currentOrganization!.id, currentUser.uid, item.chartId)
          .then(chart => chart ? { ...chart, order: item.order } as DisplayableDashboardChartOnHome : null)
      )
      Promise.all(chartPromises)
        .then(fetchedCharts => {
          setDisplayedCharts(fetchedCharts.filter(Boolean) as DisplayableDashboardChartOnHome[])
        })
        .catch(error => {
          console.error("Error fetching charts for default dashboard:", error)
          setDisplayedCharts([])
        })
        .finally(() => {
          setIsLoadingCharts(false)
        })
    } else {
      setDisplayedCharts([])
      setIsLoadingCharts(false)
    }
  }, [defaultDashboard, currentUser, currentOrganization])
  
  const handleRemoveChartFromDefaultDashboard = (chartIdToRemove: string) => {
    setDisplayedCharts(prev => prev.filter(c => c.id !== chartIdToRemove))
    // This is a view-only removal. To persist, an update to dashboard doc would be needed.
  }

  const isLoadingAny = isLoadingProfile || isLoadingDashboard || isLoadingCharts

  const openCreateDashboardDialog = () => {
    setDashboardTitle('')
    setDashboardDescription('')
    setCreateDashboardDialogState({ isOpen: true, mode: 'create', dashboard: null })
  }

  const handleCreateDashboardAndNavigate = async () => {
    if (!currentUser || !currentOrganization || !dashboardTitle.trim()) {
      console.error('User, organization, or dashboard title is missing')
      // TODO: Add user feedback (e.g., toast error)
      return
    }
    setIsSubmittingDashboard(true)
    try {
      const initialLayout: DashboardLayoutConfiguration = { columns: 3 }
      const initialCharts: DashboardChartItem[] = []
      const newDashboardData: CreateDashboardData = {
        organizationId: currentOrganization.id,
        userId: currentUser.uid,
        title: dashboardTitle.trim(),
        layoutConfiguration: initialLayout,
        charts: initialCharts,
      }
      const trimmedDescription = dashboardDescription.trim()
      if (trimmedDescription) {
        (newDashboardData as any).description = trimmedDescription
      }

      const createdDashboard = await DashboardService.createDashboard(currentOrganization.id, currentUser.uid, newDashboardData)
      
      setCreateDashboardDialogState({ isOpen: false, mode: 'create' }) // Close dialog
      // Navigate to the new dashboard
      if (createdDashboard && createdDashboard.id) {
        navigate(`/organization/${currentOrganization.id}/dashboards/${createdDashboard.id}`)
      }
    } catch (error) {
      console.error('Error creating dashboard from home page:', error)
      // TODO: Add user feedback (e.g., toast error)
    } finally {
      setIsSubmittingDashboard(false)
    }
  }

  const handleOpenChartStudiesDialogOnHomePage = (chartToOpen: Chart) => {
    console.log('Home.tsx: Opening studies dialog for chart:', chartToOpen);
    setStudiesDialogState({
      isOpen: true,
      chart: chartToOpen,
      isDatabase: true, // Charts on dashboard are from DB
    });
  };

  const handleApplyStudiesToChartOnHomePage = async (originalChart: Chart, updatedStudies: ChartStudy[], newChartType: ChartType, newAxisOptions: SharedAxisOptions | null, newDisplayOptions: { showGridLines: boolean } | null) => {
    if (!currentUser || !currentOrganization || !originalChart || !originalChart.id ) {
      setStudiesDialogState({ isOpen: false, chart: null, isDatabase: true }); // Close if context or chart is invalid
      return;
    }
    console.log('Home.tsx: Applying studies and type to chart:', originalChart.id, updatedStudies, newChartType, newAxisOptions, newDisplayOptions);
    try {
      const payload: any = {
        studies: updatedStudies,
        type: newChartType,
      };
      if (newAxisOptions !== null) {
        payload.axisOptions = newAxisOptions;
      }
      if (newDisplayOptions !== null) {
        payload.displayOptions = newDisplayOptions;
      }
      await ChartService.update(currentOrganization.id, originalChart.userId, originalChart.id, payload);
      // Re-fetch default dashboard data to reflect updated chart details
      if (userProfile?.defaultDashboardId) {
        setIsLoadingDashboard(true); // Trigger loading states for dashboard and its charts
        DashboardService.getDashboard(currentOrganization.id, currentUser.uid, userProfile.defaultDashboardId)
        .then(dashboard => {
          setDefaultDashboard(dashboard); // This will trigger the useEffect to fetch charts for this dashboard
        })
        .catch(error => {
          console.error("Error re-fetching default dashboard after study apply:", error);
          setDefaultDashboard(null);
        })
        .finally(() => {
          setIsLoadingDashboard(false);
        });
      } else {
        // Fallback if default dashboard context somehow lost, though unlikely here
        // This part might be less effective as it only updates the displayedCharts array, not the source dashboard state
        setDisplayedCharts(prev => prev.map(c => c.id === originalChart.id ? {...c, studies: updatedStudies, type: newChartType, axisOptions: newAxisOptions === null ? undefined : newAxisOptions, displayOptions: newDisplayOptions === null ? undefined : newDisplayOptions } as DisplayableDashboardChartOnHome : c));
      }
    } catch (error) {
      console.error('Home.tsx: Error applying studies/type/axis options to chart:', error);
      // TODO: Add user feedback (e.g., toast notification)
    } finally {
      setStudiesDialogState({ isOpen: false, chart: null, isDatabase: true }); // Close dialog in all cases
    }
  };

  const handleChartDataUpdateOnHomePage = () => {
    // Re-fetch default dashboard data to reflect updated chart details
    if (currentUser && currentOrganization && userProfile?.defaultDashboardId) {
      console.log('Home.tsx: Re-fetching default dashboard due to chart data update.');
      setIsLoadingDashboard(true); // Trigger loading states for dashboard and its charts
      DashboardService.getDashboard(currentOrganization.id, currentUser.uid, userProfile.defaultDashboardId)
      .then(dashboard => {
        setDefaultDashboard(dashboard); // This will trigger the useEffect to fetch charts for this dashboard
      })
      .catch(error => {
        console.error("Error re-fetching default dashboard after chart data update:", error);
        setDefaultDashboard(null);
      })
      .finally(() => {
        setIsLoadingDashboard(false);
      });
    } else {
      console.warn('Home.tsx: Could not re-fetch dashboard data, context missing.');
      // Potentially, you could try to re-fetch just the displayedCharts if that makes sense
      // For now, logging a warning.
    }
  };

  // Handle color palette changes for home screen charts
  const handleHomeChartPaletteChange = async (chartId: string, paletteId: string) => {
    if (!currentUser?.uid || !currentOrganization?.id) return;
    
    try {
      const chart = displayedCharts.find(c => c.id === chartId);
      if (!chart) return;

      await ChartService.update(currentOrganization.id, currentUser.uid, chartId, {
        configuration: {
          ...chart.configuration,
          colorPalette: paletteId,
        } as any
      });

      // Update local state immediately for visual feedback
      setDisplayedCharts(prev =>
        prev.map(c =>
          c.id === chartId
            ? {
                ...c,
                configuration: {
                  ...c.configuration,
                  colorPalette: paletteId,
                } as any,
                updatedAt: new Date()
              }
            : c
        )
      );
    } catch (error) {
      console.error('Error updating chart palette on home:', error);
    }
  };

  // Handle single color changes for home screen charts
  const handleHomeChartColorChange = async (chartId: string, color: any) => {
    if (!currentUser?.uid || !currentOrganization?.id) return;
    
    try {
      const chart = displayedCharts.find(c => c.id === chartId);
      if (!chart) return;

      await ChartService.update(currentOrganization.id, currentUser.uid, chartId, {
        configuration: {
          ...chart.configuration,
          singleColor: color,
        } as any
      });

      // Update local state immediately for visual feedback
      setDisplayedCharts(prev =>
        prev.map(c =>
          c.id === chartId
            ? {
                ...c,
                configuration: {
                  ...c.configuration,
                  singleColor: color,
                } as any,
                updatedAt: new Date()
              }
            : c
        )
      );
    } catch (error) {
      console.error('Error updating chart color on home:', error);
    }
  };

  // Handle chart title updates for home screen charts
  const handleHomeChartTitleUpdate = async (chartId: string, newTitle: string) => {
    if (!currentUser?.uid || !currentOrganization?.id) return;
    
    try {
      await ChartService.update(currentOrganization.id, currentUser.uid, chartId, {
        title: newTitle
      });

      // Update local state immediately for visual feedback
      setDisplayedCharts(prev =>
        prev.map(c =>
          c.id === chartId
            ? {
                ...c,
                title: newTitle,
                updatedAt: new Date()
              }
            : c
        )
      );
    } catch (error) {
      console.error('Error updating chart title on home:', error);
    }
  };

  // renderStudiesPreviewChart - needed for ChartStudiesDialog
  // This function should be identical to the one in DashboardViewPage.tsx or Charts.tsx, using shared utils.
  // For brevity, assuming it exists and is correctly defined using `applyStudiesToData` etc. from this file's scope or chartUtils.
  const renderStudiesPreviewChartForHome = (chartToPreview: ChartData | Chart | any, studiesToApply: ChartStudy[]): JSX.Element | null => {
    // Convert to universal format if it's a database chart
    let universalChart: ChartData;
    if (chartToPreview.datasets) {
      // It's a database chart, convert it
      universalChart = convertDatabaseChartToUniversal(chartToPreview);
    } else {
      // It's already a local/universal chart
      universalChart = chartToPreview;
    }
    
    const config = normalizeChartData(universalChart, { 
      height: 400,
      customPalettes,
      isDarkMode,
      enableAdvancedFeatures: true,
    });
    config.studies = studiesToApply;
    return <UniversalChartRenderer config={config} />;
  };

  return (
    <Sidebar>
      <div className="space-y-6">
        {/* --- Row 1: Page Header & Org Logo --- */}
        <div className="flex items-center justify-between border-b border-border pb-5">
          {/* Left: Icon, Title, Tagline */}
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <HomeIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold leading-tight text-foreground">Home</h1>
              <p className="mt-2 max-w-4xl text-sm text-muted-foreground mr-4">
                Create beautiful dashboards and charts.
              </p>
            </div>
          </div>
          {/* Right: Organization Logo */}
          <div className="flex-shrink-0">
            {currentOrganization && currentOrganization.logos && (isDarkMode && currentOrganization.logos.dark || !isDarkMode && currentOrganization.logos.light) ? (
                <img 
                    src={isDarkMode ? currentOrganization.logos.dark?.url : currentOrganization.logos.light?.url}
                    alt={`${currentOrganization.name} logo`}
                    className="h-14 w-auto object-contain"
                />
            ) : currentOrganization ? (
              <div className="text-sm font-semibold text-muted-foreground pr-2">{currentOrganization.name}</div> // Display name if no logo
            ) : null}
          </div>
        </div>


        {/* --- Row 2: Charts Stat, Default Dash Title, Create Buttons --- */}
        <div className="flex items-center justify-between mb-6 gap-4">

          {/* Center: Default Dashboard Title (if any) */}
          <div className="flex-1  px-2 min-w-0">
            {defaultDashboard && (
                <h2 className="text-2xl font-semibold text-foreground truncate" title={defaultDashboard.title}>{defaultDashboard.title}</h2>
            )}
            {!defaultDashboard && (isLoadingProfile || isLoadingDashboard) && (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto" />
            )}
            {!defaultDashboard && !isLoadingProfile && !isLoadingDashboard && userProfile && (
                  <Link to="/dashboards" className="text-sm text-primary hover:underline">Set default dashboard</Link>
            )}
          </div>
          
          {/* Right: Action Buttons */}
          <div className="flex-shrink-0 flex items-center gap-2">
            <Button onClick={() => setShowChartCreator(s => !s)} size="sm">
              <Plus className="h-4 w-4 mr-1.5" /> Create Chart
            </Button>
            {userChartCount > 0 && (
              <Button onClick={openCreateDashboardDialog} size="sm">
                <Plus className="h-4 w-4 mr-1.5" /> Create Dashboard
              </Button>
            )}
          </div>
        </div>

        {/* Chart Creator Row (Animated) */}
        {showChartCreator && (
          <div className="mb-6 overflow-hidden transition-all duration-500 ease-in-out" 
              style={{ maxHeight: showChartCreator ? '1000px' : '0px', opacity: showChartCreator ? 1:0 }}
          >
            <QuickChartCreator
              isVisible={showChartCreator}
              onClose={() => setShowChartCreator(false)}
              onSuccess={() => {
                setShowChartCreator(false);
                setUserChartCount(c => c + 1);
                navigate('/charts'); 
              }}
            />
          </div>
        )}

        {/* Main Content Area (Default Dashboard or CTA) */}
        {(isLoadingDashboard || (defaultDashboard && isLoadingCharts)) && !isLoadingProfile && (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        )}

        {!isLoadingAny && defaultDashboard && displayedCharts.length > 0 && (
          <div>
            <div className={`mt-0 grid gap-6 grid-cols-1 md:grid-cols-${defaultDashboard.layoutConfiguration.columns === 1 ? '1' : '2'} lg:grid-cols-${defaultDashboard.layoutConfiguration.columns || 3}`}>
              {displayedCharts.sort((a,b) => a.order - b.order).map(chart => (
                <DisplayChartCard 
                  key={chart.id} 
                  chart={chart} 
                  onRemoveChart={handleRemoveChartFromDefaultDashboard}
                  onOpenStudies={handleOpenChartStudiesDialogOnHomePage}
                  onChartDataUpdate={handleChartDataUpdateOnHomePage}
                  hideTitle={false}
                  hideEditButton={true}
                />
              ))}
            </div>
          </div>
        )}
        
        {!isLoadingAny && defaultDashboard && displayedCharts.length === 0 && !isLoadingCharts && (
             <Card className="text-center py-12">
                <CardContent>
                <LayoutDashboard className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Default Dashboard is Empty</h3>
                <p className="text-muted-foreground mb-6">
                    This dashboard ({defaultDashboard.title}) is currently empty. Add some charts to it!
                </p>
                <Button asChild>
                    <Link to={`/organization/${currentOrganization?.id}/dashboards/${defaultDashboard.id}`}>View & Edit Dashboard</Link>
                </Button>
                </CardContent>
            </Card>
        )}

        {!isLoadingAny && !defaultDashboard && userProfile && (
          <Card className="text-center py-12">
            <CardContent>
              <LayoutDashboard className="h-16 w-16 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Default Dashboard Set</h3>
              <p className="text-muted-foreground mb-6">
                Go to your dashboards to set one for quick access here.
              </p>
              <Button asChild>
                <Link to="/dashboards">Go to Dashboards</Link>
              </Button>
            </CardContent>
          </Card>
        )}
         {!isLoadingAny && !userProfile && !isLoadingProfile && (
            <Card className="text-center py-12">
                <CardContent>
                    <BarChart3 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">Welcome!</h3>
                    <p className="text-muted-foreground mb-6">Create a chart or set up your organization to get started.</p>
                </CardContent>
            </Card>
        )}

        {/* Create Dashboard Dialog for Home Page */}
        <Dialog open={createDashboardDialogState.isOpen && createDashboardDialogState.mode === 'create'} 
                onOpenChange={(isOpen) => setCreateDashboardDialogState(prev => ({ ...prev, isOpen }))}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New Dashboard</DialogTitle>
              <DialogDescription>
                Give your new dashboard a title and an optional description.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="home-dashboard-title" className="text-right">Title</Label>
                <Input
                  id="home-dashboard-title"
                  value={dashboardTitle}
                  onChange={(e) => setDashboardTitle(e.target.value)}
                  className="col-span-3"
                  placeholder="e.g., Q3 Sales Overview"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="home-dashboard-description" className="text-right">Description</Label>
                <Textarea
                  id="home-dashboard-description"
                  value={dashboardDescription}
                  onChange={(e) => setDashboardDescription(e.target.value)}
                  className="col-span-3"
                  placeholder="A brief summary of this dashboard's purpose."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                  <Button type="button" variant="outline" onClick={() => setCreateDashboardDialogState({isOpen: false, mode: 'create'})}>Cancel</Button>
              </DialogClose>
              <Button type="submit" onClick={handleCreateDashboardAndNavigate} disabled={isSubmittingDashboard || !dashboardTitle.trim()}>
                {isSubmittingDashboard && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Dashboard & View
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {studiesDialogState.isOpen && studiesDialogState.chart && (
          <ChartStudiesDialog
            isOpen={studiesDialogState.isOpen}
            onClose={() => setStudiesDialogState({ isOpen: false, chart: null, isDatabase: true })}
            chart={studiesDialogState.chart} 
            isDatabaseChart={studiesDialogState.isDatabase} 
            initialStudies={studiesDialogState.chart.studies || []}
            onApplyStudies={async (updatedStudies, updatedType, updatedAxisOptions, updatedDisplayOptions) => {
              if (studiesDialogState.chart) { 
                await handleApplyStudiesToChartOnHomePage(studiesDialogState.chart, updatedStudies, updatedType, updatedAxisOptions, updatedDisplayOptions);
              }
            }}
            currentUser={currentUser}
            currentOrganization={currentOrganization}
            renderPreviewFunction={renderStudiesPreviewChartForHome}
            onChartUpdate={handleChartDataUpdateOnHomePage}
            onChartTitleUpdate={handleHomeChartTitleUpdate}
            onChartPaletteChange={handleHomeChartPaletteChange}
            onChartColorChange={handleHomeChartColorChange}
          />
        )}

      </div>
    </Sidebar>
  )
}

export default Home 